import React, { useEffect, useRef, useState } from "react";
import Navbar from "../Components/Navbar";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate, useSearchParams } from "react-router-dom";
import loader from "../assets/loading.gif";

const API = process.env.REACT_APP_BASE_URL;

/**
 * Landing page for PMI's `paymentCallback`.
 * Route: /pago/completado?ref=TVP50001
 *
 * PMI redirects here after the user finishes (or abandons) checkout. The redirect
 * itself is NOT proof of payment — the webhook is. So we poll our own backend,
 * which in turn polls PMI, until the status settles.
 */

/** Keyed on the payout track — what Transferven is doing for the customer. */
const STATUS_VIEW = {
  Pendiente: {
    title: "Esperando confirmación del pago",
    body: "Estamos verificando tu pago con el proveedor. Esto puede tardar unos segundos.",
    tone: "text-muted",
    spinner: true,
  },
  Recibido: {
    title: "¡Pago recibido!",
    body: "Hemos recibido tu pago y ya estamos procesando el envío. Te avisaremos por correo cuando el beneficiario reciba el dinero.",
    tone: "text-success",
    spinner: false,
  },
  "En Proceso": {
    title: "Envío en proceso",
    body: "Tu dinero va camino del beneficiario. Te avisaremos por correo al completarse.",
    tone: "text-success",
    spinner: false,
  },
  Completada: {
    title: "¡Envío completado!",
    body: "El beneficiario ya ha recibido el dinero. Gracias por confiar en Transferven.",
    tone: "text-success",
    spinner: false,
  },
  Anulada: {
    title: "Envío anulado",
    body: "Esta orden ha sido anulada. Si hubiera algún pago recibido, te lo devolveremos a la misma cuenta de origen.",
    tone: "text-danger",
    spinner: false,
  },
};

// "Pendiente" is the only non-settled state — everything else means the
// payment landed (or failed) and there's nothing left to wait for here.
const SETTLED = ["Recibido", "En Proceso", "Completada", "Anulada"];
const MAX_POLLS = 20; // ~60s at 3s intervals

const PagoCompletado = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const reference = params.get("ref");
  // PayTKS sends the customer back here with result=failed when checkout fails
  // or is abandoned (we register separate success/failed callback URLs).
  const checkoutFailed = params.get("result") === "failed";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState("");
  const pollCount = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!reference) {
      setError("No hemos podido identificar tu orden.");
      setLoading(false);
      return;
    }

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/pmi/order/${reference}`, {
          headers: {
            "Content-Type": "application/json",
            token: Cookies.get("token"),
          },
        });

        const o = data?.order;
        setOrder(o);
        setLoading(false);

        if (o && SETTLED.includes(o.status)) return; // done
        // Checkout failed and nothing has landed — no point polling for a minute.
        if (checkoutFailed && o?.status === "Pendiente") return;

        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) {
          setTimedOut(true);
          return;
        }
        timer.current = setTimeout(poll, 3000);
      } catch (err) {
        console.error(err);
        setError(
          err?.response?.data?.msg || "No hemos podido consultar el estado de tu orden."
        );
        setLoading(false);
      }
    };

    poll();
    return () => clearTimeout(timer.current);
  }, [reference]);

  const fmt = (n, d = 2) =>
    Number(n || 0).toLocaleString("es-ES", {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });

  const paymentFailed = checkoutFailed && order?.status === "Pendiente";

  const view = paymentFailed
    ? {
        title: "El pago no se ha completado",
        body: "No hemos recibido tu pago, así que tu envío no se ha procesado. Puedes volver a intentarlo cuando quieras. Si ves un cargo en tu cuenta, te lo confirmaremos por correo en cuanto lo recibamos.",
        tone: "text-danger",
        spinner: false,
      }
    : STATUS_VIEW[order?.status] ||
    (timedOut
      ? {
          title: "Seguimos verificando",
          body: "Tu pago está tardando más de lo habitual en confirmarse. Puedes consultar el estado en tu historial de órdenes; te avisaremos por correo.",
          tone: "text-muted",
          spinner: false,
        }
      : STATUS_VIEW.Pendiente);

  return (
    <div className="OrderSection" style={{ minHeight: "100vh" }}>
      <Navbar />

      <div className="profile_box px-md-0 px-3 mx-auto py-5 mt-10 bg-white rounded">
        <div className="calculator pb-0">
          {loading ? (
            <img src={loader} alt="loading..." className="loader mt-5" />
          ) : error ? (
            <>
              <h4 className="text-danger">Algo ha ido mal</h4>
              <p className="mt-2">{error}</p>
              <button
                className="btn bg-p text-white px-4 mt-3"
                onClick={() => navigate("/orderhistory")}
              >
                Ver mis órdenes
              </button>
            </>
          ) : (
            <>
              <h4 className={view.tone}>{view.title}</h4>
              <p className="mt-2">{view.body}</p>

              {view.spinner && !timedOut && (
                <img
                  src={loader}
                  alt="verificando..."
                  className="loader"
                  style={{ width: 60 }}
                />
              )}

              {order && (
                <div className="mt-4 border rounded p-3">
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted">Referencia</span>
                    <span className="fw-bold">{order.reference}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted">Beneficiario</span>
                    <span className="fw-bold">{order.beneficiary?.bname}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted">Banco</span>
                    <span className="fw-bold">{order.beneficiary?.bbank}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted">Enviado</span>
                    <span className="fw-bold">{fmt(order.gross)} €</span>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted">Recibe</span>
                    <span className="fw-bold">
                      {fmt(order.receives)} {order.currencyTo}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between py-1 border-top mt-2 pt-2">
                    <span className="text-muted">Estado</span>
                    <span className="fw-bold">{order.status}</span>
                  </div>
                </div>
              )}

              <div className="mt-4 d-flex flex-wrap gap-2">
                <button
                  className="btn bg-p text-white px-4"
                  onClick={() => navigate("/orderhistory")}
                >
                  Ver mis órdenes
                </button>
                <button
                  className="btn btn-secondary px-4"
                  onClick={() => navigate("/prealertar")}
                >
                  Nuevo envío
                </button>
              </div>

              {(order?.status === "Anulada" || order?.pmiStatus === "INCIDENCE") && (
                <p className="small mt-3 mb-0">
                  ¿Necesitas ayuda?{" "}
                  <a
                    href="https://wa.me/34620818181"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-danger fw-bold"
                  >
                    Contacta con Atención al Cliente
                  </a>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PagoCompletado;